import React, { useRef, useState } from "react";
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import Tooltip from "../../../Components/Tooltip";
import { useDevice } from "../../../_layout";
import {
  RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT,
  RECIPE_EDITOR_NAME_INPUT_HEIGHT,
  RECIPE_EDITOR_TYPE_CHIP_HEIGHT,
  RECIPE_TYPE_OPTIONS,
} from "../recipeLogic";
import { MAX_NUTRITION_NAME_LENGTH } from "../../nutritionLimits";
import { getLocalizedAllergenLabel } from "../../shared/localizedAllergens";
import { useDesktopAutoFocusTextInput } from "../../shared/useDesktopAutoFocusTextInput";

function getRecipeSuitabilityCopy(t, recipeClassification) {
  if (recipeClassification?.isVegan) {
    return t("vegan-string", "Vegan");
  }

  if (recipeClassification?.isVegetarian) {
    return t("vegetarian-string", "Vegetarian");
  }

  return t("none-string", "None");
}

function RecipeTypeChip({ icon, label, isActive, onPress }) {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
      style={styles.typeChipTouchable}
    >
      <View
        style={[
          styles.typeChip,
          isHovered && !isActive ? styles.typeChipHover : null,
          isActive ? styles.typeChipActive : null,
        ]}
      >
        <Ionicons
          name={icon}
          size={14}
          color={isActive ? Colors.bubbleColor : Colors.darkgrayColor}
        />
        <Text
          style={[
            styles.typeChipText,
            isActive ? styles.typeChipTextActive : null,
          ]}
        >
          {label}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

export function RecipeEditorDetailsSection({
  t,
  form,
  updateFormField,
  recipeClassification,
}) {
  const { isMobile } = useDevice();
  const [isNameInputHovered, setIsNameInputHovered] = useState(false);
  const nameInputRef = useRef(null);
  const shouldAutoFocusNameRef = useRef(
    String(form?.name ?? "").trim().length === 0,
  );
  const allergens = Array.isArray(recipeClassification?.allergens)
    ? recipeClassification.allergens
    : [];
  const suitabilityLabel = getRecipeSuitabilityCopy(t, recipeClassification);
  const summaryTooltipText = t(
    "recipeDerivedFromFoodsTooltip-string",
    "Imported from the foods added to this recipe.",
  );
  const allergensLabel =
    allergens.length > 0
      ? allergens.map((allergenCode) => getLocalizedAllergenLabel(allergenCode, t)).join(", ")
      : t("none-string", "None");
  useDesktopAutoFocusTextInput(nameInputRef, {
    enabled: shouldAutoFocusNameRef.current,
    isMobile,
  });

  return (
    <View style={styles.formGrid}>
      <View style={styles.formFieldRow}>
        <View style={styles.formFieldMain}>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>{t("name-string")}</Text>
            <View
              onMouseEnter={
                !isMobile ? () => setIsNameInputHovered(true) : undefined
              }
              onMouseLeave={
                !isMobile ? () => setIsNameInputHovered(false) : undefined
              }
              style={[
                styles.nameInputShell,
                isNameInputHovered ? styles.nameInputShellHovered : null,
              ]}
            >
              <TextInput
                ref={nameInputRef}
                value={form.name}
                onChangeText={(value) => updateFormField("name", value)}
                maxLength={MAX_NUTRITION_NAME_LENGTH}
                placeholder={t("recipeNamePlaceholder-string")}
                placeholderTextColor={Colors.grayColor}
                style={styles.nameInput}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>{t("recipeType-string")}</Text>
            <View style={styles.typeChipRow}>
              {RECIPE_TYPE_OPTIONS.map((option) => (
                <RecipeTypeChip
                  key={option.key}
                  icon={option.icon}
                  label={t(option.key)}
                  isActive={form.typeKey === option.key}
                  onPress={() => updateFormField("typeKey", option.key)}
                />
              ))}
            </View>
          </View>
        </View>
      </View>

      <View style={styles.summaryRow}>
        <View style={styles.summarySection}>
          <View style={styles.summaryInlineRow}>
            <Text style={styles.fieldLabel}>
              {t(
                "nutritionFoodContainsAllergens-string",
                "Contains allergens",
              )}:{" "}
            </Text>
            <Tooltip content={summaryTooltipText} placement="top" rawText={true}>
              <View style={styles.summaryValueWrap}>
                <Text style={styles.summaryValueText}>{allergensLabel}</Text>
              </View>
            </Tooltip>
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.summaryInlineRow}>
            <Text style={styles.fieldLabel}>
              {t(
                "nutritionFoodDietarySuitability-string",
                "Dietary suitability",
              )}:{" "}
            </Text>
            <Tooltip content={summaryTooltipText} placement="top" rawText={true}>
              <View style={styles.summaryValueWrap}>
                <Text style={styles.summaryValueText}>{suitabilityLabel}</Text>
              </View>
            </Tooltip>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  formGrid: {
    gap: 16,
  },
  formFieldRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  formFieldMain: {
    flex: 1,
    minWidth: 240,
    gap: 16,
  },
  formField: {
    gap: 10,
  },
  summaryRow: {
    flexDirection: "column",
    alignItems: "flex-start",
    rowGap: 10,
  },
  summarySection: {
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
    alignItems: "flex-start",
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "600",
    lineHeight: RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT,
    color: Colors.darkgrayColor,
    letterSpacing: 0.2,
  },
  summaryInlineRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
    columnGap: 0,
    rowGap: 2,
    width: "100%",
  },
  nameInputShell: {
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    minHeight: RECIPE_EDITOR_NAME_INPUT_HEIGHT,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    transition:
      "border-color 180ms ease-in-out, background-color 180ms ease-in-out",
  },
  nameInputShellHovered: {
    borderColor: Colors.grayColor,
  },
  nameInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 10,
  },
  preparationTimeShell: {
    width: 120,
  },
  preparationTimeInput: {
    flex: 0,
    width: 28,
    textAlign: "right",
  },
  preparationTimeUnit: {
    fontSize: 13,
    color: Colors.grayColor,
    marginLeft: 4,
  },
  summaryValueWrap: {
    maxWidth: "100%",
    alignItems: "flex-start",
  },
  summaryValueText: {
    ...Fonts.regular13,
    fontSize: 12,
    fontStyle: "italic",
    fontWeight: "400",
    color: Colors.grayColor,
    lineHeight: 18,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  typeChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  typeChipTouchable: {
    flex: 1,
    minWidth: 150,
    borderRadius: 12,
  },
  typeChip: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    minHeight: RECIPE_EDITOR_TYPE_CHIP_HEIGHT,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    transition:
      "border-color 200ms ease-in-out, background-color 200ms ease-in-out",
  },
  typeChipHover: {
    borderColor: Colors.grayColor,
  },
  typeChipActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  typeChipText: {
    flexShrink: 1,
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    textAlign: "center",
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  typeChipTextActive: {
    color: Colors.bubbleColor,
  },
});
